import { useCallback, useEffect, useRef, type PointerEvent, type WheelEvent } from 'react';

interface AreaTagSetLassoInputProps {
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  onChange: (points: number[][]) => void;
  onDrawingChange?: (drawing: boolean) => void;
  onWheel?: (event: WheelEvent<HTMLDivElement>) => void;
}

interface ClientPoint {
  clientX: number;
  clientY: number;
}

const SAMPLE_DISTANCE_PX = 4;

export const clientToLayoutPoint = (
  svg: SVGSVGElement,
  group: SVGGElement,
  clientX: number,
  clientY: number
): [number, number] | null => {
  const screenMatrix = group.getScreenCTM();
  if (!screenMatrix) return null;

  const point = svg.createSVGPoint();
  point.x = clientX;
  point.y = clientY;
  const layoutPoint = point.matrixTransform(screenMatrix.inverse());
  return [layoutPoint.x, layoutPoint.y];
};

export const AreaTagSetLassoInput = ({
  svgRef,
  groupRef,
  onChange,
  onDrawingChange,
  onWheel,
}: AreaTagSetLassoInputProps) => {
  const activePointerIdRef = useRef<number | null>(null);
  const pointsRef = useRef<number[][]>([]);
  const lastClientPointRef = useRef<ClientPoint | null>(null);

  const toLayoutPoint = useCallback(
    (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      const group = groupRef.current;
      return svg && group ? clientToLayoutPoint(svg, group, clientX, clientY) : null;
    },
    [groupRef, svgRef]
  );

  const appendSample = useCallback(
    (sample: ClientPoint, force = false) => {
      const previous = lastClientPointRef.current;
      if (
        !force &&
        previous &&
        Math.hypot(sample.clientX - previous.clientX, sample.clientY - previous.clientY) <
          SAMPLE_DISTANCE_PX
      ) {
        return;
      }

      const layoutPoint = toLayoutPoint(sample.clientX, sample.clientY);
      if (!layoutPoint) return;
      lastClientPointRef.current = sample;
      pointsRef.current = [...pointsRef.current, layoutPoint];
      onChange(pointsRef.current);
    },
    [onChange, toLayoutPoint]
  );

  const finishDrawing = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (activePointerIdRef.current !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      appendSample(event, true);

      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      activePointerIdRef.current = null;
      lastClientPointRef.current = null;
      if (pointsRef.current.length < 3) {
        pointsRef.current = [];
        onChange([]);
      }
      onDrawingChange?.(false);
    },
    [appendSample, onChange, onDrawingChange]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      activePointerIdRef.current = null;
      pointsRef.current = [];
      lastClientPointRef.current = null;
      onChange([]);
      onDrawingChange?.(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onChange, onDrawingChange]);

  return (
    <div
      data-layout-overlay
      data-testid="area-tag-set-lasso-input"
      role="application"
      aria-label="Draw around units or locations to create a phase or zone"
      tabIndex={0}
      className="absolute inset-0 z-20 cursor-crosshair select-none"
      style={{ touchAction: 'none' }}
      onContextMenu={(event) => event.preventDefault()}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onWheel={onWheel}
      onPointerDown={(event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        activePointerIdRef.current = event.pointerId;
        pointsRef.current = [];
        lastClientPointRef.current = null;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        onDrawingChange?.(true);
        appendSample(event, true);
      }}
      onPointerMove={(event) => {
        if (activePointerIdRef.current !== event.pointerId) return;
        event.preventDefault();
        event.stopPropagation();
        const samples = event.nativeEvent.getCoalescedEvents?.() ?? [event.nativeEvent];
        samples.forEach((sample) => appendSample(sample));
      }}
      onPointerUp={finishDrawing}
      onPointerCancel={finishDrawing}
    />
  );
};
